import React from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  useTheme as useMuiTheme,
  alpha,
  Dialog,
  DialogContent,
  DialogTitle,
  Backdrop,
} from '@mui/material';
import { CheckCircle, Error, Warning } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { Inference } from '../hooks/inferenceHooks';

interface ServiceHealthCheckProps {
  serviceName: string;
  customMessage?: string;
  onServiceHealthy?: () => void;
  onServiceUnhealthy?: () => void;
  open: boolean;
  onClose?: () => void;
  onCloseWithDocumentManager?: () => void;
}

type ServiceStatus = 'healthy' | 'unhealthy' | 'loading' | 'error';

export const ServiceHealthCheck: React.FC<ServiceHealthCheckProps> = ({
  serviceName,
  customMessage,
  onServiceHealthy,
  onServiceUnhealthy,
  open,
  onClose,
  onCloseWithDocumentManager,
}) => {
  const { darkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const { data: healthCheckData, error: healthCheckError, isLoading } = Inference.useHealthCheck();

  // Determine service status
  const getServiceStatus = (): ServiceStatus => {
    if (isLoading) return 'loading';
    if (healthCheckError) return 'error';
    if (healthCheckData?.message?.includes('UNHEALTHY')) return 'unhealthy';
    if (healthCheckData?.message?.includes('HEALTHY')) return 'healthy';
    return 'error';
  };

  const status = getServiceStatus();

  // Get status icon and color
  const getStatusDisplay = () => {
    switch (status) {
      case 'healthy':
        return {
          icon: <CheckCircle sx={{ fontSize: 16 }} />,
          color: muiTheme.palette.success.main,
          label: 'Healthy',
          backgroundColor: alpha(muiTheme.palette.success.main, 0.1),
        };
      case 'unhealthy':
        return {
          icon: <Warning sx={{ fontSize: 16 }} />,
          color: muiTheme.palette.warning.main,
          label: 'Starting Up',
          backgroundColor: alpha(muiTheme.palette.warning.main, 0.1),
        };
      case 'error':
        return {
          icon: <Error sx={{ fontSize: 16 }} />,
          color: muiTheme.palette.error.main,
          label: 'Error',
          backgroundColor: alpha(muiTheme.palette.error.main, 0.1),
        };
      case 'loading':
      default:
        return {
          icon: <CircularProgress size={16} />,
          color: muiTheme.palette.primary.main,
          label: 'Checking',
          backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  // Call callbacks when status changes
  React.useEffect(() => {
    if (status === 'healthy' && onServiceHealthy) {
      onServiceHealthy();
    } else if ((status === 'unhealthy' || status === 'error') && onServiceUnhealthy) {
      onServiceUnhealthy();
    }
  }, [status, onServiceHealthy, onServiceUnhealthy]);

  // Handle dialog close - when clicking outside, close both dialog and document manager
  const handleDialogClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      // When clicking outside, close both the dialog and the document manager
      if (onCloseWithDocumentManager) {
        onCloseWithDocumentManager();
      }
    } else if (onClose) {
      // For escape key or other close events, just close the dialog
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          backgroundColor: darkMode
            ? alpha(muiTheme.palette.background.paper, 0.95)
            : muiTheme.palette.background.paper,
          border: `1px solid ${alpha(statusDisplay.color, 0.3)}`,
          boxShadow: `0 8px 32px ${alpha(statusDisplay.color, 0.15)}`,
        },
      }}
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: {
          backgroundColor: alpha(muiTheme.palette.background.default, 0.8),
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          pb: 1,
          borderBottom: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Chip
            icon={statusDisplay.icon}
            label={statusDisplay.label}
            size="medium"
            sx={{
              backgroundColor: statusDisplay.backgroundColor,
              color: statusDisplay.color,
              border: `1px solid ${alpha(statusDisplay.color, 0.3)}`,
              fontWeight: 500,
              '& .MuiChip-icon': {
                color: statusDisplay.color,
              },
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: muiTheme.palette.text.primary,
              fontWeight: 600,
            }}
          >
            {serviceName}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          textAlign: 'center',
          pt: 3,
          pb: 4,
        }}
      >
        {customMessage && (
          <Typography
            variant="body1"
            sx={{
              color: muiTheme.palette.text.secondary,
              fontSize: '1.1rem',
              lineHeight: 1.6,
            }}
          >
            {customMessage}
          </Typography>
        )}

        {status === 'loading' && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: muiTheme.palette.text.disabled,
                fontSize: '0.9rem',
              }}
            >
              Checking service status...
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
